'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { H5 } from 'foundations-components/transitional/components/H5';
import text from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { createTicket } from 'calling-lifecycle-internal/zorse/zorseControls';
import { getAsyncAccountVerificationCanAppeal, getHasAppealed, getIsAppealStateRejected } from '../operators/callingAccessGetters';
import PortalIdParser from 'PortalIdParser';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { logPageAction } from 'calling-error-reporting/report/error';
import { hasPaidSeatAccess } from 'calling-orchestration-shared-library/hub-info/utils/hasCallingFeature';
import { Link } from 'foundations-components/transitional/components/Link';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
class CallingSuspensionMessage extends PureComponent {
  constructor() {
    super();
    this.handleOpenSupportWindow = () => {
      createTicket();
    };
    this.hasPaidSeat = hasPaidSeatAccess();
    this.setOpenAppealForm = this.setOpenAppealForm.bind(this);
  }
  setOpenAppealForm() {
    logPageAction({
      key: 'CALLING_APPEAL_MODAL_OPEN',
      tags: {
        subType: 'calling-access'
      }
    });
    CommunicatorLogger.log('callingInteraction', {
      action: 'Click Appeal',
      activity: 'call',
      channel: 'outbound call',
      source: this.props.appIdentifier
    });
    window.location.href = `/account-verification/${PortalIdParser.get()}?source=CALLING`;
  }
  renderAlert() {
    return /*#__PURE__*/_jsx(Alert, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "callingSuspensionMessage.alreadyAppealTitle"
      }),
      type: "tip",
      marginBlock: "300",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "callingSuspensionMessage.alreadyAppealText"
      })
    });
  }
  renderAppealAction() {
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "callingSuspensionMessage.message"
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Button, {
          onClick: this.setOpenAppealForm,
          use: 'tertiary',
          marginBlock: "600",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "callingSuspensionMessage.appealButton"
          })
        })
      })]
    });
  }
  renderRejected() {
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "callingSuspensionMessage.rejectedText"
      })
    });
  }
  renderAppealState() {
    const {
      callingAccess
    } = this.props;
    const alreadyAppealed = getHasAppealed(callingAccess);
    const isRejected = getIsAppealStateRejected(callingAccess);
    if (isRejected) {
      return this.renderRejected();
    } else if (!alreadyAppealed) {
      return this.renderAppealAction();
    }
    return null;
  }
  renderNoAppeal() {
    let langKey = 'callingPaid';
    let deactivated_options = {
      onClick: this.handleOpenSupportWindow
    };
    if (!this.hasPaidSeat) {
      langKey = 'callingFree';
      deactivated_options = {
        href: 'https://knowledge.hubspot.com/account-settings/account-suspension-faq',
        target: '_blank'
      };
    }
    const deactivated_lang = `twilio.notEnabled.error.deactivated.info.${langKey}_jsx`;
    return /*#__PURE__*/_jsx(TFlex, {
      direction: "column",
      justify: "center",
      padding: "400",
      flex: 1,
      maxInlineSize: "520px",
      margin: "auto",
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        illustration: "keys",
        illustrationProps: {
          height: 100
        },
        title: text('twilio.notEnabled.error.deactivated.title'),
        children: /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: deactivated_lang,
            options: deactivated_options,
            elements: {
              UILink: Link
            }
          })
        })
      })
    });
  }
  renderFullWidth() {
    const {
      callingAccess
    } = this.props;
    const alreadyAppealed = getHasAppealed(callingAccess);
    const canAppeal = getAsyncAccountVerificationCanAppeal(callingAccess);
    const isRejected = getIsAppealStateRejected(callingAccess);
    if (!canAppeal && !alreadyAppealed) {
      return this.renderNoAppeal();
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [alreadyAppealed && !isRejected && this.renderAlert(), /*#__PURE__*/_jsx(TFlex, {
        direction: "column",
        justify: "center",
        padding: "400",
        flex: 1,
        maxInlineSize: "520px",
        margin: "auto",
        children: /*#__PURE__*/_jsx(ErrorMessage, {
          illustration: "lock",
          illustrationProps: {
            width: '25%'
          },
          title: isRejected ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "callingSuspensionMessage.rejectedTitle"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "callingSuspensionMessage.title"
          }),
          children: this.renderAppealState()
        })
      })]
    });
  }
  renderPopoverView() {
    const {
      callingAccess
    } = this.props;
    const alreadyAppealed = getHasAppealed(callingAccess);
    const canAppeal = getAsyncAccountVerificationCanAppeal(callingAccess);
    const isRejected = getIsAppealStateRejected(callingAccess);
    if (!canAppeal && !alreadyAppealed) {
      return this.renderNoAppeal();
    }
    return /*#__PURE__*/_jsxs(TFlex, {
      direction: "column",
      justify: "start",
      padding: "400",
      children: [alreadyAppealed && !isRejected && this.renderAlert(), /*#__PURE__*/_jsxs(TFlex, {
        justify: "start",
        marginBlock: "300",
        children: [/*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Illustration, {
            name: "lock",
            width: alreadyAppealed && !isRejected ? 100 : 250
          })
        }), /*#__PURE__*/_jsxs(TFlex, {
          alignSelf: "center",
          textAlign: "left",
          marginInlineStart: "400",
          direction: "column",
          children: [/*#__PURE__*/_jsx(H5, {
            marginBlockEnd: "200",
            children: isRejected ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "callingSuspensionMessage.rejectedTitle"
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "callingSuspensionMessage.title"
            })
          }), this.renderAppealState()]
        })]
      })]
    });
  }
  render() {
    if (this.props.useFullWidth) {
      return this.renderFullWidth();
    } else {
      return this.renderPopoverView();
    }
  }
}
CallingSuspensionMessage.propTypes = {
  callingAccess: PropTypes.object.isRequired,
  useFullWidth: PropTypes.bool,
  appIdentifier: PropTypes.string.isRequired
};
export default CallingSuspensionMessage;